import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  VStack, 
  Heading, 
  Text, 
  Button, 
  HStack,
  Grid
} from '@chakra-ui/react';

// Custom useInterval hook
const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const NotFound = () => {
  const [glitchText, setGlitchText] = useState('404');
  const [countdown, setCountdown] = useState(10);
  const [autoRedirect, setAutoRedirect] = useState(true);

  // Glitch effect for 404 text
  useInterval(() => {
    if (Math.random() > 0.8) {
      const glitchChars = ['404', '4Ø4', '4O4', '4∅4', '₄₀₄', '４０４'];
      setGlitchText(glitchChars[Math.floor(Math.random() * glitchChars.length)]);
      setTimeout(() => setGlitchText('404'), 150);
    }
  }, 1000);

  // Auto redirect countdown
  useEffect(() => {
    if (!autoRedirect || countdown <= 0) return;

    const timer = setTimeout(() => {
      if (countdown === 1) {
        window.location.href = '/';
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, autoRedirect]);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const handleStopRedirect = () => {
    setAutoRedirect(false);
  };

  // Suggested pages
  const suggestedPages = [
    { name: 'Home', path: '/', description: 'Our main page with services overview' },
    { name: 'Engineers', path: '/engineers', description: 'Meet our engineering team' },
    { name: 'Projects', path: '/projects', description: 'View our portfolio and case studies' },
    { name: 'Articles', path: '/articles', description: 'Technical articles and insights' },
    { name: 'About', path: '/about', description: 'Learn more about our company' },
    { name: 'Contact', path: '/contact', description: 'Get in touch with us' }
  ];

  return (
    <Box py={32} px={4} minH="90vh">
      <VStack spacing={48} maxW="6xl" mx="auto">
        {/* Main 404 Section */}
        <VStack spacing={16} textAlign="center">
          {/* Glitchy 404 Text */}
          <Box position="relative" mb={8}>
            <Heading 
              size="4xl" 
              textTransform="uppercase" 
              letterSpacing="wide"
              fontSize={{ base: '6xl', md: '9xl' }}
              color="green.500"
              fontFamily="mono"
              textShadow="0 0 20px rgba(72, 187, 120, 0.5)"
              transition="all 0.2s"
            >
              {glitchText}
            </Heading>
            
            {/* Glitch overlay effect */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              background="linear-gradient(90deg, transparent 30%, rgba(72, 187, 120, 0.1) 50%, transparent 70%)"
              animation="glitch 3s infinite"
              pointerEvents="none"
            />
          </Box>

          <VStack spacing={8}>
            <Heading 
              size="2xl" 
              textTransform="uppercase" 
              letterSpacing="wide"
              color="white"
            >
              PAGE NOT FOUND
            </Heading>
            
            <Text fontSize="xl" color="gray.300" maxW="2xl" lineHeight="1.6">
              The page you're looking for seems to have vanished into the digital void. 
              Don't worry though - our engineering team is always building new solutions.
            </Text>

            <Text fontSize="lg" color="gray.400">
              ERROR: RESOURCE_NOT_FOUND | STATUS: 404 | TIMESTAMP: {new Date().toISOString()}
            </Text>
          </VStack>
        </VStack>

        {/* Action Buttons */}
        <VStack spacing={8}>
          <HStack spacing={6} flexWrap="wrap" justify="center">
            <Button
              bg="green.500"
              color="white"
              _hover={{ bg: "green.600" }}
              onClick={handleGoHome}
              textTransform="uppercase"
              letterSpacing="wide"
              size="lg"
              px={8}
            >
              Go Home
            </Button>
            
            <Button
              variant="outline"
              borderColor="gray.600"
              color="white"
              _hover={{ borderColor: "green.500", color: "green.500" }}
              onClick={handleGoBack}
              textTransform="uppercase"
              letterSpacing="wide"
              size="lg"
              px={8}
            >
              Go Back
            </Button>
          </HStack>

          {/* Auto redirect notice */}
          {autoRedirect && countdown > 0 && (
            <VStack spacing={2}>
              <Text color="gray.400" fontSize="sm">
                Redirecting to home page in {countdown} seconds...
              </Text>
              <Button
                variant="ghost"
                size="sm"
                color="gray.500"
                _hover={{ color: "gray.400" }}
                onClick={handleStopRedirect}
              >
                Cancel auto-redirect
              </Button>
            </VStack>
          )}
        </VStack>

        {/* Suggested Pages */}
        <VStack spacing={12} w="100%">
          <VStack spacing={6} textAlign="center">
            <Heading 
              size="xl" 
              textTransform="uppercase" 
              letterSpacing="wide"
              color="white"
            >
              Explore Our Site
            </Heading>
            <Text color="gray.300">
              Here are some pages you might be interested in:
            </Text>
          </VStack>

          <Grid 
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} 
            gap={8} 
            w="100%"
          >
            {suggestedPages.map((page) => (
              <Box
                key={page.name}
                bg="gray.900"
                p={6}
                border="1px solid"
                borderColor="gray.700"
                _hover={{ 
                  borderColor: "green.500", 
                  bg: "gray.800",
                  transform: "translateY(-2px)" 
                }}
                transition="all 0.3s"
                cursor="pointer"
                onClick={() => window.location.href = page.path}
              >
                <VStack align="start" spacing={3}>
                  <HStack spacing={2}>
                    <Box w={2} h={2} bg="green.500" borderRadius="full" />
                    <Heading 
                      size="md" 
                      textTransform="uppercase" 
                      letterSpacing="wide"
                      color="white"
                    >
                      {page.name}
                    </Heading>
                  </HStack>
                  
                  <Text color="gray.300" fontSize="sm" lineHeight="1.6">
                    {page.description}
                  </Text>
                  
                  <Text 
                    color="green.500" 
                    fontSize="xs" 
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Visit Page →
                  </Text>
                </VStack>
              </Box>
            ))}
          </Grid>
        </VStack>

        {/* Technical Info */}
        <VStack spacing={6} textAlign="center">
          <Text fontSize="sm" color="gray.500">
            If you believe this is an error, please contact our support team.
          </Text>
          
          <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap" justify="center">
            <Text>Server: CLOVER-RIDGE-01</Text>
            <Text>•</Text>
            <Text>Build: {process.env.REACT_APP_VERSION || 'v1.0.0'}</Text>
            <Text>•</Text>
            <Text>Ref: {window.location.pathname}</Text>
          </HStack>
        </VStack>
      </VStack>

      {/* Custom CSS for glitch animation */}
      <style jsx>{`
        @keyframes glitch {
          0% { transform: translateX(0); }
          20% { transform: translateX(-2px); }
          40% { transform: translateX(2px); }
          60% { transform: translateX(-1px); }
          80% { transform: translateX(1px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </Box>
  );
};

export default NotFound;